import React, { useRef } from "react";
import { motion, useInView } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import clsx from "clsx";

export const Container = ({ className, children }) => (
  <div className={clsx("mx-auto w-full max-w-[1320px] px-5 sm:px-8 lg:px-12", className)}>
    {children}
  </div>
);

export const Eyebrow = ({ children, className, dark }) => (
  <div className={clsx("flex items-center gap-3", className)}>
    <span className="h-px w-8 bg-govx-green" />
    <span className={clsx("eyebrow", dark ? "text-govx-grey" : "text-govx-green")}>{children}</span>
  </div>
);

// Line-by-line masked reveal for headings
export const RevealHeading = ({ lines, className, delay = 0, as: Tag = "h1" }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.2 });
  return (
    <Tag ref={ref} className={className}>
      {lines.map((line, i) => (
        <span key={i} className="block overflow-hidden pb-[0.06em]">
          <motion.span
            className="block"
            initial={{ y: "115%" }}
            animate={inView ? { y: "0%" } : { y: "115%" }}
            transition={{ duration: 0.85, ease: [0.16, 1, 0.3, 1], delay: delay + i * 0.09 }}
          >
            {line}
          </motion.span>
        </span>
      ))}
    </Tag>
  );
};

export const Reveal = ({ children, className, delay = 0, y = 28 }) => (
  <motion.div
    className={className}
    initial={{ opacity: 0, y }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, amount: 0.2 }}
    transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1], delay }}
  >
    {children}
  </motion.div>
);

export const PillButton = ({ to, href, children, variant = "solid", className, onClick, type, dark, ...rest }) => {
  const base =
    "group inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-semibold tracking-tight transition-colors duration-300";
  const styles = {
    solid: "bg-govx-green text-white hover:bg-govx-green-deep",
    outline: dark
      ? "border border-white/25 text-white hover:border-govx-lime hover:text-govx-lime"
      : "border border-govx-charcoal/25 text-govx-charcoal hover:border-govx-green hover:text-govx-green",
    ghost: dark ? "text-white hover:text-govx-lime" : "text-govx-charcoal hover:text-govx-green",
  };
  const cls = clsx(base, styles[variant], className);
  const inner = (
    <>
      {children}
      <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
    </>
  );
  if (to) return <Link to={to} className={cls} onClick={onClick} {...rest}>{inner}</Link>;
  if (href) return <a href={href} className={cls} onClick={onClick} {...rest}>{inner}</a>;
  return <button type={type || "button"} className={cls} onClick={onClick} {...rest}>{inner}</button>;
};

export const TextLink = ({ to, children, className, dark }) => (
  <Link
    to={to}
    className={clsx(
      "group inline-flex items-center gap-1.5 text-sm font-semibold tracking-tight transition-colors duration-300",
      dark ? "text-white hover:text-govx-lime" : "text-govx-green hover:text-govx-green-deep",
      className
    )}
  >
    {children}
    <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
  </Link>
);

export const Section = ({ id, className, dark, children }) => (
  <section
    id={id}
    className={clsx(
      "relative",
      dark ? "bg-govx-black text-govx-paper grain" : "bg-govx-paper text-govx-charcoal",
      className
    )}
  >
    {children}
  </section>
);

export const StatBar = ({ label, dark }) => (
  <span className={clsx("font-mono text-[0.68rem] uppercase tracking-[0.18em]", dark ? "text-govx-grey" : "text-govx-grey")}>
    {label}
  </span>
);
